<template>
  <!--
    Junta os botões flutuantes num só. SALVIA-944/940: sem gatilho próprio (o
    antigo "⋯"/X). O primeiro atalho presente na ordem — o "+" de novo
    atendimento, para o médico — fica sempre à vista no lugar dele (data-base)
    e é ele que abre a coluna: passar o mouse, focar pelo teclado ou, no
    toque, o primeiro toque (o segundo aciona o botão).

    Recebe os atalhos por slot e os reposiciona redefinindo as variáveis
    --ds-fab-bottom-N, que eles já usam. Cada atalho mantém a própria regra de
    quando aparecer; o dock só empilha OS QUE APARECERAM (ver `empilhar`).
  -->
  <div
    v-show="temAtalhos"
    class="fab-dock"
    :class="{ 'fab-dock--aberto': aberto }"
    :data-base="base"
    :style="estiloDaColuna"
    @mouseenter="aberto = true"
    @mouseleave="aberto = false"
    @focusin="aberto = true"
    @focusout="aberto = false"
    @pointerdown.capture="ultimoPonteiro = $event.pointerType"
    @click.capture="primeiroToqueAbre"
  >
    <slot></slot>
  </div>
</template>

<script>
/**
 * Ordem da coluna, de baixo para cima, e a variável de posição que cada atalho
 * lê (tokens.css). SALVIA-944/940 (Vidal, 29/10): + → IA → recados → "?".
 *
 * O PRIMEIRO presente é a base: ocupa o lugar do antigo gatilho, fica sempre à
 * vista e abre os demais. Quem não aparece não deixa buraco — a assistente não
 * vê o + nem a IA (são de médico), e para ela a base vira o de recados.
 */
const ATALHOS = [
  { nome: "na", seletor: ".na-fab", variavel: "--ds-fab-bottom-3" }, //  novo atendimento
  { nome: "cal", seletor: ".cal-fab", variavel: "--ds-fab-bottom-2" }, // salvIA
  { nome: "anw", seletor: ".anw-fab", variavel: "--ds-fab-bottom-0" }, // recados
  { nome: "gt", seletor: ".gt-fab", variavel: "--ds-fab-bottom-1" }, //  "?" do tour
];

export default {
  name: "FloatingDock",

  data() {
    return {
      aberto: false,
      // Há pelo menos um atalho NA TELA? Sem nenhum, o dock some.
      temAtalhos: false,
      // Variáveis de posição de cada atalho presente (ver `empilhar`).
      estiloDaColuna: {},
      // Qual atalho está na base, sempre à vista (nome em ATALHOS).
      base: null,
      // "mouse" | "touch" | "pen" — no toque, o primeiro toque só abre a coluna.
      ultimoPonteiro: null,
    };
  },

  watch: {
    aberto(v) {
      // No toque não há mouseleave: fecha ao tocar fora da coluna.
      if (v) document.addEventListener("pointerdown", this.fecharSeTocouFora, true);
      else document.removeEventListener("pointerdown", this.fecharSeTocouFora, true);
    },
  },

  mounted() {
    this.empilhar();

    // Os atalhos decidem aparecer DEPOIS de montados — o de recados, por
    // exemplo, só existe quando a lista de destinatários chega da API. Isso
    // não re-renderiza o dock, então ele observa a própria árvore.
    this.observador = new MutationObserver(() => this.empilhar());
    this.observador.observe(this.$el, { childList: true, subtree: true });
  },

  updated() {
    this.empilhar();
  },

  beforeDestroy() {
    this.observador?.disconnect();
    document.removeEventListener("pointerdown", this.fecharSeTocouFora, true);
  },

  methods: {
    /**
     * No toque não existe "passar o mouse": sem isto, tocar na base acionaria
     * direto o + e IA, recados e "?" ficariam inalcançáveis no celular. Então o
     * primeiro toque só abre a coluna; com ela aberta, o toque segue normal.
     */
    primeiroToqueAbre(event) {
      if (this.aberto || this.ultimoPonteiro !== "touch") return;
      event.preventDefault();
      event.stopPropagation();
      this.aberto = true;
    },

    fecharSeTocouFora(event) {
      if (!this.$el.contains(event.target)) this.aberto = false;
    },

    /**
     * Empilha os atalhos PRESENTES, um em cima do outro, sem andar vazio.
     *
     * A base fica no andar 0 (onde era o gatilho); o n-ésimo atalho presente
     * vai para o n-ésimo andar acima dela. Fechado, os demais ficam escondidos
     * atrás da base (CSS). A altura da área que segura o dock aberto acompanha
     * o número de atalhos.
     */
    empilhar() {
      const presentes = ATALHOS.filter((a) => this.$el.querySelector(a.seletor));
      const tem = presentes.length > 0;
      const base = tem ? presentes[0].nome : null;

      // Só atribui quando muda: `updated` que sempre escreve entra em laço.
      if (tem !== this.temAtalhos) this.temAtalhos = tem;
      if (base !== this.base) this.base = base;
      if (!tem && this.aberto) this.aberto = false;

      const andar = "(var(--ds-fab-h-0, 56px) + var(--ds-fab-gap, 8px))";
      const estilo = {
        "--dock-altura-aberto": `calc(var(--ds-fab-h-0, 56px) + ${Math.max(presentes.length - 1, 0)} * ${andar})`,
      };
      presentes.forEach((a, i) => {
        estilo[`--dock-andar-${a.variavel.slice(-1)}`] = `calc(var(--dock-base) + ${i} * ${andar})`;
      });

      if (JSON.stringify(estilo) !== JSON.stringify(this.estiloDaColuna)) {
        this.estiloDaColuna = estilo;
      }
    },
  },
};
</script>

<style scoped>
/*
  O contêiner precisa ter tamanho de verdade: `:hover` não dispara em caixa
  vazia, e os filhos são `position: fixed` (saem do fluxo, não dão altura ao
  pai). Colapsado ele ocupa só a área do gatilho — o mesmo espaço que os FABs
  já ocupavam. Aberto, cresce para cobrir a coluna inteira e o ponteiro pode
  atravessar o vão entre um botão e outro sem fechar tudo no meio do caminho.
*/
.fab-dock {
  /*
    `--dock-base` existe para o dock NÃO se posicionar por uma variável que ele
    próprio redefine. Usar --ds-fab-bottom-0 aqui criava um laço: abrir movia a
    variável, o dock subia junto, saía de baixo do cursor, disparava mouseleave,
    fechava, voltava — e piscava sem parar.
  */
  --dock-base: 24px;

  position: fixed;
  right: var(--ds-fab-right, 24px);
  bottom: var(--dock-base);
  width: var(--ds-fab-h-0, 56px);
  height: var(--ds-fab-h-0, 56px);
  z-index: var(--ds-fab-z, 95);

  /*
    O "?" nasceu menor que os outros (44px). Dentro do dock ele fica do mesmo
    tamanho — a coluna com um botão menor no meio ficava desalinhada.
  */
  --ds-fab-h-1: var(--ds-fab-h-0, 56px);

  /* Colapsado: todos encostam no gatilho, escondidos atrás dele. */
  --ds-fab-bottom-0: var(--dock-base);
  --ds-fab-bottom-1: var(--dock-base);
  --ds-fab-bottom-2: var(--dock-base);
  --ds-fab-bottom-3: var(--dock-base);
}

.fab-dock--aberto {
  /*
    Cobre a coluna aberta, para o ponteiro atravessar os vãos sem fechar.
    A altura vem do script: cresce e encolhe com o número de atalhos na tela.
  */
  height: var(--dock-altura-aberto, 320px);

  /*
    Cada atalho presente recebe o andar calculado em `empilhar` (sem buraco
    para quem não apareceu). O andar é publicado em --dock-andar-N para o
    `calc` desta regra ficar fora do alcance de quem redefine --ds-fab-*.
  */
  --ds-fab-bottom-0: var(--dock-andar-0, var(--dock-base));
  --ds-fab-bottom-1: var(--dock-andar-1, var(--dock-base));
  --ds-fab-bottom-2: var(--dock-andar-2, var(--dock-base));
  --ds-fab-bottom-3: var(--dock-andar-3, var(--dock-base));
}

/*
  Esconde os botões, não os componentes: cada um deles embrulha também os
  próprios diálogos, e apagar o embrulho apagaria o diálogo aberto junto.
  Por isso a mira é nos FABs (.anw-fab, .cal-fab, .gt-fab, .na-fab), nunca
  nos pais.
*/
.fab-dock ::v-deep .anw-fab,
.fab-dock ::v-deep .cal-fab,
.fab-dock ::v-deep .gt-fab,
.fab-dock ::v-deep .na-fab {
  opacity: 0;
  pointer-events: none;
  transform: translateY(10px) scale(0.85);
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.fab-dock--aberto ::v-deep .anw-fab,
.fab-dock--aberto ::v-deep .cal-fab,
.fab-dock--aberto ::v-deep .gt-fab,
.fab-dock--aberto ::v-deep .na-fab {
  opacity: 1;
  pointer-events: auto;
  transform: none;
}

/* A base (primeiro atalho presente) fica sempre à vista — é o "gatilho". */
.fab-dock[data-base="na"] ::v-deep .na-fab,
.fab-dock[data-base="cal"] ::v-deep .cal-fab,
.fab-dock[data-base="anw"] ::v-deep .anw-fab,
.fab-dock[data-base="gt"] ::v-deep .gt-fab {
  opacity: 1;
  pointer-events: auto;
  transform: none;
}

/* O glifo acompanha o círculo maior, senão fica pequeno no meio dele. */
.fab-dock ::v-deep .gt-fab__q {
  font-size: 26px;
}

@media (prefers-reduced-motion: reduce) {
  .fab-dock ::v-deep .anw-fab,
  .fab-dock ::v-deep .cal-fab,
  .fab-dock ::v-deep .gt-fab,
  .fab-dock ::v-deep .na-fab {
    transition: none;
    transform: none;
  }
}
</style>
